import React, { useState, useEffect } from 'react';

import { Button } from '@shared/components/bootstrap';
import { Spacer } from '@shared/components/helpers';

import { ConfirmModal } from '@admin/components/helpers/confirm_modal';
import { Panel } from '@admin/components/helpers/panel';
import { getWarehouseOrDepotInput } from '@admin/components/shifts/utilities';
import { Spinner } from '@admin/components/spinner';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { HIRING } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  Day,
  HiringInterviewTimeSlotsQuery,
  useHiringInterviewFacilitiesQuery,
  useHiringInterviewTimeSlotDestroyMutation,
  useHiringInterviewTimeSlotsQuery,
  useHiringInterviewCandidateInstructionsUpdateMutation,
} from '@admin/schema';
import {
  decodeWarehouseOrDepot,
  encodeWarehouseOrDepot,
  warehouseOrDepotNameWithType,
} from '@admin/utils/warehouse_or_depots';

import { CreateTimeSlotModal } from './create_modal';

type TimeSlot = HiringInterviewTimeSlotsQuery['hiringInterviewTimeSlots'][0];

export const HiringInterviewTimeSlotsCalendar: React.FC = () => {
  useTitle('Hiring | Interview Time Slots');
  const canUpdate = usePermission(HIRING.resource, HIRING.actions.update);
  const {
    data: facilitiesData,
    loading: loadingFacilities,
    refetch: refetchFacilities,
  } = useHiringInterviewFacilitiesQuery({ client });
  const [encodedWarehouseOrDepot, setEncodedWarehouseOrDepot] = useState<string | undefined>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Day | undefined>(undefined);
  const [confirmModal, setConfirmModal] = useState<{ message: string; onConfirm: () => void } | undefined>(undefined);
  const [deleteTimeSlot] = useHiringInterviewTimeSlotDestroyMutation({ client });
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [candidateInstructions, setCandidateInstructions] = useState<string>('');
  const [updateCandidateInstructions] = useHiringInterviewCandidateInstructionsUpdateMutation({ client });

  const {
    data: timeSlotsData,
    loading: loadingTimeSlots,
    refetch: refetchTimeSlots,
  } = useHiringInterviewTimeSlotsQuery({
    client,
    variables: { warehouseOrDepot: getWarehouseOrDepotInput(encodedWarehouseOrDepot)! },
    skip: !encodedWarehouseOrDepot,
  });

  const timeSlots = timeSlotsData?.hiringInterviewTimeSlots;

  const getTimeSlotsByDay = () => {
    if (!timeSlots) return undefined;

    const timeSlotsByDay: Record<Day, TimeSlot[]> = {
      [Day.Sunday]: [],
      [Day.Monday]: [],
      [Day.Tuesday]: [],
      [Day.Wednesday]: [],
      [Day.Thursday]: [],
      [Day.Friday]: [],
      [Day.Saturday]: [],
    };

    timeSlots.forEach((timeSlot) => {
      if (timeSlotsByDay[timeSlot.day]) {
        timeSlotsByDay[timeSlot.day].push(timeSlot);
      }
    });

    // Sort time slots within each day
    Object.keys(timeSlotsByDay).forEach((day) => {
      timeSlotsByDay[day as Day].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    });

    return timeSlotsByDay;
  };

  const handleAddTimeSlot = (day: Day) => {
    setSelectedDay(day);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = (refetchTimeSlotData: boolean) => {
    setIsCreateModalOpen(false);
    setSelectedDay(undefined);
    if (refetchTimeSlotData) {
      refetchTimeSlots();
    }
  };

  const handleDeleteTimeSlot = async (id: string) => {
    setConfirmModal({
      message:
        'Are you sure you want to delete this time slot? Any interviews already scheduled will not be affected, but new interviews will no longer be booked for this time slot.',
      onConfirm: async () => {
        await deleteTimeSlot({ variables: { id } });
        refetchTimeSlots();
        setConfirmModal(undefined);
      },
    });
  };

  const timeSlotsByDay = getTimeSlotsByDay();
  const selectedWarehouseOrDepot =
    facilitiesData &&
    encodedWarehouseOrDepot &&
    decodeWarehouseOrDepot(facilitiesData.warehouseOrDepots, encodedWarehouseOrDepot);
  const orderedDays = [Day.Sunday, Day.Monday, Day.Tuesday, Day.Wednesday, Day.Thursday, Day.Friday, Day.Saturday];

  // Update candidate instructions when facility changes
  useEffect(() => {
    if (selectedWarehouseOrDepot && selectedWarehouseOrDepot.candidateInterviewInstructions) {
      setCandidateInstructions(selectedWarehouseOrDepot.candidateInterviewInstructions);
    } else {
      setCandidateInstructions('');
    }
  }, [selectedWarehouseOrDepot]);

  const handleSaveInstructions = async () => {
    if (!selectedWarehouseOrDepot) return;

    await updateCandidateInstructions({
      variables: {
        warehouseOrDepot: getWarehouseOrDepotInput(encodedWarehouseOrDepot)!,
        instructions: candidateInstructions,
      },
    });
    setIsEditingInstructions(false);
    refetchFacilities();
  };

  if (loadingFacilities) {
    return <Spinner />;
  }

  return (
    <>
      <Panel>
        <Panel.Body>
          <select
            id="facilitySelect"
            className="form-select form-select-sm"
            style={{ width: '280px' }}
            value={encodedWarehouseOrDepot ?? ''}
            onChange={(e) => {
              setEncodedWarehouseOrDepot(e.target.value === '' ? undefined : e.target.value);
            }}
          >
            <option value=""> - Select Facility - </option>
            {facilitiesData?.warehouseOrDepots.map((facility) => (
              <option key={encodeWarehouseOrDepot(facility)} value={encodeWarehouseOrDepot(facility)}>
                {warehouseOrDepotNameWithType(facility)}
              </option>
            ))}
          </select>
        </Panel.Body>
      </Panel>
      <Spacer />
      <Panel>
        <Panel.Body>
          {loadingTimeSlots && <Spinner />}

          {timeSlotsByDay && encodedWarehouseOrDepot && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
              {orderedDays.map((day) => (
                <div key={day}>
                  <h6 style={{ textAlign: 'center' }}>{day}</h6>
                  <div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {timeSlotsByDay[day].map((timeSlot) => (
                        <div
                          key={timeSlot.id}
                          style={{
                            textAlign: 'center',
                            borderRadius: '8px',
                            padding: '4px',
                            backgroundColor: 'teal',
                            color: 'white',
                            position: 'relative',
                          }}
                        >
                          {timeSlot.formattedStartTime} - {timeSlot.formattedEndTime}
                          <div style={{ fontSize: '0.8em', marginTop: '4px' }}>
                            {timeSlot.phone ? 'Phone' : 'Onsite'}
                          </div>
                          <div style={{ fontSize: '0.8em', marginTop: '4px' }}>
                            {timeSlot.candidateCapacity} Candidate Capacity
                          </div>
                          {canUpdate && (
                            <Button
                              onClick={() => handleDeleteTimeSlot(timeSlot.id)}
                              style={{
                                position: 'absolute',
                                top: '-4px',
                                right: '0px',
                                color: 'white',
                                width: '20px',
                                height: '20px',
                              }}
                            >
                              &times;
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    {canUpdate && (
                      <div style={{ marginTop: '10px', textAlign: 'center' }}>
                        <Button
                          size="sm"
                          outline={true}
                          kind="primary"
                          onClick={() => handleAddTimeSlot(day)}
                          style={{
                            borderRadius: '8px',
                          }}
                        >
                          Add Time Slot
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel.Body>
      </Panel>

      {selectedWarehouseOrDepot && (
        <>
          <Spacer height="8px" />
          <h3>Candidate Interview Instructions</h3>
          <Panel>
            <Panel.Header>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '16px 16px 0 16px',
                }}
              >
                {canUpdate && (
                  <div style={{ justifyContent: 'flex-end', display: 'flex', gap: '2px', width: '100%' }}>
                    <Button
                      kind="primary"
                      onClick={() => setIsEditingInstructions(!isEditingInstructions)}
                      style={{ marginBottom: '16px' }}
                    >
                      {isEditingInstructions ? 'Cancel Editing' : 'Edit Instructions'}
                    </Button>
                  </div>
                )}
              </div>
            </Panel.Header>
            <Panel.Body>
              <textarea
                className="form-control"
                value={candidateInstructions}
                onChange={(e) => setCandidateInstructions(e.target.value)}
                rows={5}
                disabled={!isEditingInstructions}
                placeholder="Enter instructions for candidates on how to access the facility, as well as any information they should know before arriving, such as what to bring."
              />
            </Panel.Body>
            {isEditingInstructions && (
              <Panel.Footer align="right">
                <Button type="button" kind="primary" onClick={handleSaveInstructions}>
                  Save
                </Button>
              </Panel.Footer>
            )}
          </Panel>
        </>
      )}

      {isCreateModalOpen && selectedWarehouseOrDepot && selectedDay && (
        <CreateTimeSlotModal
          closeModal={handleCloseModal}
          day={selectedDay}
          warehouseOrDepot={selectedWarehouseOrDepot}
        />
      )}

      {confirmModal && <ConfirmModal {...confirmModal} onCancel={() => setConfirmModal(undefined)} />}
    </>
  );
};
